import type { GroupAccessItem, GroupDetail } from '@ralph/contracts'
import clsx from 'clsx'
import {
  ArrowLeft,
  BookOpen,
  Bot,
  Crown,
  Globe,
  IdCard,
  Lock,
  Network,
  PencilLine,
  Plug,
  Search,
  Trash2,
  UserMinus,
  UserPlus,
  UsersRound,
  X
} from 'lucide-react'
import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import {
  AddPeopleDialog,
  AppointManagerDialog,
  DeleteGroupDialog,
  EditGroupDialog,
  RemoveManagerDialog,
  RemoveMemberDialog
} from '@/components/groups/GroupDialogs.tsx'
import { Initials } from '@/components/people/PersonPicker.tsx'
import { Badge, Button, EmptyState, IconButton, Input, Spinner } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import { useGroupMembers } from '@/hooks/use-groups.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * One group, as its page shows it (PRD-26 *The interface*).
 *
 * In order: who the reader is to it (a relation line — "You manage this group but are not in it,
 * so you do not get what is shared with it" is the sentence that stops a manager assuming they
 * can see what their group can), what is shared with it, what it owns, its managers, and its
 * members. Outsiders see its name, icon, size and who to ask; everything else is for insiders and
 * the server says so by leaving it out.
 */

const KIND_ICON: Record<GroupAccessItem['kind'], typeof Bot> = {
  knowledge_base: BookOpen,
  agent: Bot,
  connected_system: Plug,
  remote_agent: Network
}

const REACH_ICON: Record<GroupAccessItem['reach'], typeof Lock> = {
  private: Lock,
  groups: UsersRound,
  everyone: Globe,
  nobody: Lock
}

const hrefFor = (item: GroupAccessItem): string | null => {
  if (!item.canOpen) return null
  if (item.kind === 'knowledge_base') return `/knowledge/${item.id}`
  if (item.kind === 'agent') return `/agents/${item.id}`
  return null
}

type Dialog =
  | { kind: 'edit' }
  | { kind: 'delete' }
  | { kind: 'appoint' }
  | { kind: 'add' }
  | { kind: 'remove'; person: { id: string; displayName: string } }
  | { kind: 'unappoint'; person: { id: string; displayName: string } }

export const GroupPanel = ({
  group,
  isLoading,
  missing,
  onRetry,
  onBack
}: {
  group: GroupDetail | undefined
  isLoading: boolean
  missing: boolean
  /** Set when the group could not be read for any reason but its absence: said in words, with a retry. */
  onRetry?: (() => void) | undefined
  onBack?: () => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.groups
  const session = useSession()
  const me = session.data?.user
  const [dialog, setDialog] = useState<Dialog | null>(null)

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 px-5 py-6 text-[13px] text-fg-subtle sm:px-8">
        <Spinner /> {t.detail.loading}
      </p>
    )
  }
  // Below lg the list is hidden while a group is open, so every state here keeps the way back.
  const back = onBack && (
    <button
      type="button"
      onClick={onBack}
      className="mb-4 inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg lg:hidden"
    >
      <ArrowLeft size={13} aria-hidden />
      {t.page.back}
    </button>
  )
  if (missing) {
    return (
      <div className="px-5 py-8 sm:px-8">
        {back}
        <EmptyState title={t.detail.missing} body={t.detail.missingBody} />
      </div>
    )
  }
  if (!group && onRetry) {
    return (
      <div className="px-5 py-8 sm:px-8">
        {back}
        <EmptyState title={t.detail.failed} body={t.detail.failedBody} />
        <div className="mt-3 flex justify-center">
          <Button size="sm" onClick={onRetry}>
            {t.detail.retry}
          </Button>
        </div>
      </div>
    )
  }
  if (!group) {
    return (
      <div className="hidden px-8 py-10 lg:block">
        <p className="max-w-sm text-pretty text-[13.5px] leading-[1.55] text-fg-muted">{t.page.pickOne}</p>
      </div>
    )
  }

  const insider = group.sharedWith !== null
  const managerNames = group.managers.map((manager) => manager.displayName)
  const sharedBases = (group.sharedWith ?? []).filter((item) => item.kind === 'knowledge_base').length
  const sharedAgents = (group.sharedWith ?? []).filter((item) => item.kind === 'agent').length

  return (
    <article aria-labelledby={`group-${group.id}`} className="fade-in px-5 pb-10 pt-5 sm:px-8">
      {back}

      <header className="flex flex-wrap items-start gap-x-4 gap-y-3">
        <GroupAvatar group={group} size={44} />
        {/* A basis, so on a phone the actions wrap below rather than squeezing the name into a
            column one word wide. */}
        <div className="min-w-0 flex-1 basis-[12rem]">
          <h2 id={`group-${group.id}`} className="display text-[26px] leading-tight sm:text-[30px]">
            {group.name}
          </h2>
          <p className="readout mt-1 text-fg-subtle">{t.detail.members(fmt, group.memberCount)}</p>
          <p className="mt-2 max-w-xl text-pretty text-[13.5px] leading-[1.55] text-fg-muted">
            {t.relation[group.relation]}
          </p>
          {!insider &&
            (managerNames.length > 0 ? (
              <p className="mt-1 text-[13.5px] leading-[1.55]">{t.detail.toJoin(fmt, managerNames)}</p>
            ) : (
              <p className="mt-1 text-[13.5px] leading-[1.55] text-fg-muted">{t.detail.nobodyManages}</p>
            ))}
        </div>
        {group.can.administer && (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" icon={<PencilLine size={13} aria-hidden />} onClick={() => setDialog({ kind: 'edit' })}>
              {t.detail.edit}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 size={13} aria-hidden />}
              onClick={() => setDialog({ kind: 'delete' })}
            >
              {t.detail.delete}
            </Button>
          </div>
        )}
      </header>

      {insider && (
        <>
          <AccessSection
            title={t.detail.sharedWith(group.name)}
            lead={t.detail.sharedWithLead(group.name)}
            empty={t.detail.sharedWithEmpty(group.name)}
            items={group.sharedWith ?? []}
          />
          <AccessSection
            title={t.detail.ownedBy(group.name)}
            lead={t.detail.ownedByLead(group.name)}
            empty={t.detail.ownedByEmpty(group.name)}
            items={group.ownedBy ?? []}
          />
        </>
      )}

      <section aria-labelledby={`managers-${group.id}`} className="mt-8">
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
          <div className="min-w-0 flex-1">
            <h3 id={`managers-${group.id}`} className="text-[15px] font-semibold tracking-[-0.018em]">
              {t.detail.managers}
            </h3>
            <p className="mt-0.5 max-w-xl text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
              {t.detail.managersLead}
            </p>
          </div>
          {group.can.administer && (
            <Button size="sm" icon={<Crown size={13} aria-hidden />} onClick={() => setDialog({ kind: 'appoint' })}>
              {t.detail.appoint}
            </Button>
          )}
        </div>
        {group.managers.length === 0 ? (
          <p className="mt-3 text-[13px] text-fg-muted">{t.detail.noManagers}</p>
        ) : (
          <ul className="mt-3 flex flex-wrap gap-2">
            {group.managers.map((manager) => (
              <li
                key={manager.id}
                className="inline-flex items-center gap-2 rounded-full border border-rule bg-card-solid py-1 pl-1 pr-2.5 text-[13px]"
              >
                <Initials person={{ avatarInitials: initialsOf(manager.displayName) }} size={20} />
                <span className="font-medium tracking-[-0.012em]">{manager.displayName}</span>
                {manager.id === me?.id && <span className="readout text-fg-subtle">{t.detail.you}</span>}
                {group.can.administer && (
                  <IconButton
                    label={t.detail.removeManager(manager.displayName)}
                    size={20}
                    tone="alert"
                    onClick={() => setDialog({ kind: 'unappoint', person: manager })}
                  >
                    <X size={10} aria-hidden />
                  </IconButton>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <MembersSection
        group={group}
        insider={insider}
        onAdd={() => setDialog({ kind: 'add' })}
        onRemove={(person) => setDialog({ kind: 'remove', person })}
        meId={me?.id ?? null}
        canOpenPeople={(me?.roles ?? []).some((role) => role === 'admin' || role === 'operator' || role === 'auditor')}
      />

      {dialog?.kind === 'edit' && <EditGroupDialog group={group} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'delete' && <DeleteGroupDialog group={group} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'appoint' && (
        <AppointManagerDialog
          group={group}
          managerIds={group.managers.map((manager) => manager.id)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'add' && (
        <AddPeopleDialog
          group={group}
          access={insider ? { bases: sharedBases, agents: sharedAgents } : null}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'remove' && (
        <RemoveMemberDialog group={group} person={dialog.person} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'unappoint' && (
        <RemoveManagerDialog group={group} person={dialog.person} onClose={() => setDialog(null)} />
      )}
    </article>
  )
}

const initialsOf = (name: string): string => {
  const words = name.split(/\s+/).filter(Boolean)
  const first = words[0]?.[0] ?? ''
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : (words[0]?.[1] ?? '')
  return (first + last).toUpperCase()
}

const AccessSection = ({
  title,
  lead,
  empty,
  items
}: {
  title: string
  lead: string
  empty: string
  items: GroupAccessItem[]
}) => {
  const fmt = useI18n()
  const t = fmt.t.groups
  const headingId = useId()
  return (
    <section aria-labelledby={headingId} className="mt-8">
      <h3 id={headingId} className="text-[15px] font-semibold tracking-[-0.018em]">
        {title}
      </h3>
      <p className="mt-0.5 max-w-xl text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{lead}</p>
      {items.length === 0 ? (
        <p className="mt-3 text-[13px] text-fg-muted">{empty}</p>
      ) : (
        <ul className="mt-3 divide-y divide-rule overflow-hidden rounded-tile border border-rule bg-card-solid">
          {items.map((item) => {
            const KindIcon = KIND_ICON[item.kind]
            const ReachIcon = REACH_ICON[item.reach]
            const href = hrefFor(item)
            const name = <span className="truncate text-[13px] font-semibold tracking-[-0.012em]">{item.name}</span>
            // The whole row, not a lookup by `reach`: a knowledge base is feminine in
            // Portuguese and an agent is not, so the sentence needs the kind (PRD-27 D-21).
            const reach = t.detail.reach(fmt, item)
            return (
              <li key={`${item.kind}-${item.id}`} className="flex items-center gap-3 px-3 py-2.5">
                <span
                  aria-hidden
                  className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-tile border border-rule bg-card text-fg-muted"
                >
                  <KindIcon size={13} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-2">
                    {href ? (
                      <Link to={href} className="min-w-0 truncate text-fg hover:underline">
                        {name}
                      </Link>
                    ) : (
                      name
                    )}
                  </span>
                  <span className="readout mt-0.5 flex flex-wrap items-center gap-x-1.5 text-fg-subtle">
                    <span>{t.detail.kinds[item.kind]}</span>
                    <span aria-hidden>·</span>
                    <span className={clsx('inline-flex items-center gap-1', item.reach === 'nobody' && 'text-alert')}>
                      <ReachIcon size={9} aria-hidden />
                      {reach}
                    </span>
                    {!item.canOpen && (item.kind === 'knowledge_base' || item.kind === 'agent') && (
                      <>
                        <span aria-hidden>·</span>
                        <span>{t.detail.cannotOpen}</span>
                      </>
                    )}
                  </span>
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

const MembersSection = ({
  group,
  insider,
  onAdd,
  onRemove,
  meId,
  canOpenPeople
}: {
  group: GroupDetail
  insider: boolean
  onAdd: () => void
  onRemove: (person: { id: string; displayName: string }) => void
  meId: string | null
  /** The People page reads this group's members with their roles (`/people?group=…`). */
  canOpenPeople: boolean
}) => {
  const fmt = useI18n()
  const t = fmt.t.groups
  const headingId = useId()
  const searchId = useId()
  const [q, setQ] = useState('')
  const members = useGroupMembers(group.id, q.trim(), insider)
  const items = (members.data?.pages ?? []).flatMap((page) => page.items)

  return (
    <section aria-labelledby={headingId} className="mt-8">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          <h3 id={headingId} className="text-[15px] font-semibold tracking-[-0.018em]">
            {t.detail.members_}
            <span aria-hidden className="readout ml-2 text-fg-subtle">
              {fmt.formatCount(group.memberCount)}
            </span>
            {/* The heading also names the section: "Members, 12 people", not "Members12". */}
            <span className="sr-only">, {t.detail.members(fmt, group.memberCount)}</span>
          </h3>
          <p className="mt-0.5 max-w-xl text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
            {insider ? t.detail.membersLead : canOpenPeople ? t.detail.membersOnPeople : t.detail.membersHidden}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canOpenPeople && (
            <Link
              to={`/people?group=${encodeURIComponent(group.id)}`}
              aria-label={t.detail.openInPeopleLabel(group.name)}
              className="inline-flex h-[30px] items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold tracking-[-0.014em] text-fg-muted transition-colors hover:bg-card-solid hover:text-fg"
            >
              <IdCard size={13} aria-hidden />
              {t.detail.openInPeople}
            </Link>
          )}
          {group.can.manageMembers && (
            <Button size="sm" variant="primary" icon={<UserPlus size={13} aria-hidden />} onClick={onAdd}>
              {t.detail.addPeople}
            </Button>
          )}
        </div>
      </div>

      {insider && (
        <>
          <div className="relative mt-3 max-w-sm">
            <label htmlFor={searchId} className="sr-only">
              {t.detail.searchMembers}
            </label>
            <Search
              size={13}
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
            />
            <Input
              id={searchId}
              value={q}
              placeholder={t.detail.searchMembersPlaceholder}
              className="pl-8"
              onChange={(event) => setQ(event.target.value)}
            />
          </div>

          {members.isLoading ? (
            <p className="mt-3 flex items-center gap-2 text-[13px] text-fg-subtle">
              <Spinner />
            </p>
          ) : members.isError ? (
            // Not "Nobody is in this group yet": a failed read is not an empty group.
            <p role="alert" className="mt-3 flex flex-wrap items-center gap-2 text-[13px] text-alert">
              {t.detail.membersFailed}
              <Button size="sm" variant="ghost" onClick={() => void members.refetch()}>
                {t.detail.retry}
              </Button>
            </p>
          ) : items.length === 0 ? (
            <p className="mt-3 text-[13px] text-fg-muted">
              {q.trim() === '' ? t.detail.noMembers : t.detail.noMemberMatch(q.trim())}
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-rule overflow-hidden rounded-tile border border-rule bg-card-solid">
              {items.map((member) => (
                <li key={member.id} className="flex items-center gap-3 px-3 py-2.5">
                  <Initials person={member} size={28} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="truncate text-[13px] font-semibold tracking-[-0.012em]">
                        {member.displayName}
                      </span>
                      {member.id === meId && <Badge tone="primary">{t.detail.you}</Badge>}
                      {member.isManager && <Badge>{t.detail.manages}</Badge>}
                    </span>
                    <span className="block truncate text-[12px] text-fg-muted">{member.email}</span>
                    <span className="readout block text-fg-subtle">
                      {t.detail.addedBy(member.addedByName, fmt.formatAgo(member.addedAt))}
                    </span>
                  </span>
                  {group.can.manageMembers && (
                    <IconButton
                      label={t.detail.removeMember(member.displayName)}
                      tone="alert"
                      size={26}
                      onClick={() => onRemove(member)}
                    >
                      <UserMinus size={12} aria-hidden />
                    </IconButton>
                  )}
                </li>
              ))}
            </ul>
          )}

          {members.hasNextPage && (
            <div className="mt-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={members.isFetchingNextPage}
                onClick={() => void members.fetchNextPage()}
              >
                {t.detail.loadMore}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
